import { Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import {
  Button,
  Form,
  InlineLoading,
  InlineNotification,
  Stack,
  TextArea,
  TextInput,
  TextInputSkeleton,
} from '@carbon/react';
import { ImageField } from 'src/components/Storefront/ImageField';
import { useClearSavedState } from 'src/hooks/useClearSavedState';
import { useStorefront, useUpdateStorefrontMutation } from 'src/hooks/useStorefront';
import { StorefrontSeoDto } from 'src/services/apis/types';

/**
 * The caps the API enforces, restated so the field blocks before the request rather than after.
 * They are not arbitrary: a `<title>` past ~60 characters is truncated in results, and a
 * description past ~160 likewise, so the limits are the medium's rather than the database's.
 */
const TITLE_MAX = 120;
const DESCRIPTION_MAX = 320;

const Schema = z.object({
  title: z.string().max(TITLE_MAX).optional(),
  description: z.string().max(DESCRIPTION_MAX).optional(),
  ogImageFileId: z.string().nullish(),
});

export interface StorefrontSeoValues {
  title: string;
  description: string;
  ogImageFileId: string | null;
}

/**
 * A cleared image is sent as `null`, NOT dropped — the same rule `Appearance.tsx` documents at
 * length. `JSON.stringify` removes an `undefined` key, and the server merges the patch onto the
 * stored value, so an absent key means "leave it alone": clearing the OG image would say "Saved"
 * and change nothing.
 */
export function toSeoPayload(values: StorefrontSeoValues): StorefrontSeoDto {
  return {
    title: values.title,
    description: values.description,
    ogImageFileId: values.ogImageFileId,
  } as StorefrontSeoDto;
}

/**
 * What search results and social cards show.
 *
 * These three fields have been stored, validated, published and rendered since the storefront
 * shipped — `apps/storefront/src/lib/metadata.ts` reads all three into `<title>`, the meta
 * description and `og:image` — but no screen ever wrote them, so every site has been serving the
 * derived fallbacks (restaurant name, slogan, hero image). This is the missing writer, not a new
 * capability.
 *
 * Empty is a legitimate answer and deliberately not defaulted here: the app already falls back to
 * the restaurant's own name and slogan, and pre-filling the form with those values would make an
 * owner's "I left it blank" indistinguishable from "I typed the same thing", freezing a copy of
 * the restaurant name that no longer tracks it.
 */
const StorefrontSeo = React.memo(() => {
  const { t } = useTranslation();
  const { data: storefront, isLoading } = useStorefront();
  const {
    mutate: updateStorefront,
    isLoading: saving,
    isSuccess: saved,
    error: updateError,
    reset: resetUpdate,
  } = useUpdateStorefrontMutation();

  useClearSavedState(saved, resetUpdate);

  const seo = (storefront?.seo ?? {}) as Partial<StorefrontSeoValues>;

  return (
    <Stack gap={6}>
      <p className="storefront-seo__hint">{t('storefront.seo.hint')}</p>
      {isLoading && <TextInputSkeleton />}
      {!isLoading && storefront && (
        <Formik<StorefrontSeoValues>
          enableReinitialize
          validationSchema={toFormikValidationSchema(Schema)}
          initialValues={{
            title: seo.title ?? '',
            description: seo.description ?? '',
            ogImageFileId: seo.ogImageFileId ?? null,
          }}
          onSubmit={(values) => updateStorefront({ seo: toSeoPayload(values) })}
        >
          {({ handleSubmit, values, errors, setFieldValue, handleChange, isValid }) => (
            <Form onSubmit={handleSubmit}>
              <Stack gap={6}>
                <TextInput
                  id="seoTitle"
                  name="title"
                  labelText={t('storefront.seo.metaTitle')}
                  helperText={t('storefront.seo.metaTitleHint')}
                  value={values.title}
                  invalid={Boolean(errors.title)}
                  invalidText={t('storefront.seo.tooLong')}
                  onChange={handleChange}
                />

                <TextArea
                  id="seoDescription"
                  name="description"
                  labelText={t('storefront.seo.metaDescription')}
                  helperText={t('storefront.seo.metaDescriptionHint')}
                  rows={3}
                  value={values.description}
                  invalid={Boolean(errors.description)}
                  invalidText={t('storefront.seo.tooLong')}
                  onChange={handleChange}
                />

                {/* 1200×630 is the Open Graph card ratio every major platform crops to, which is
                    why this one is 16:9-ish rather than square like the logo. */}
                <ImageField
                  title={t('storefront.seo.ogImage')}
                  description={t('storefront.seo.ogImageHint')}
                  aspectRatio={'16x9'}
                  cropperAspectRatio={1200 / 630}
                  value={values.ogImageFileId}
                  onChange={(fileId) => setFieldValue('ogImageFileId', fileId)}
                />

                <div className="storefront-seo__actions">
                  {saving || saved ? (
                    <InlineLoading
                      description={saved ? t('common.saved') : `${t('common.saving')}...`}
                      status={saved ? 'finished' : 'active'}
                    />
                  ) : (
                    <Button type="submit" disabled={!isValid}>
                      {t('storefront.actions.save')}
                    </Button>
                  )}
                </div>

                {updateError ? <InlineNotification kind="error" subtitle={t('messages.somethingWentWrong')} /> : null}
              </Stack>
            </Form>
          )}
        </Formik>
      )}
    </Stack>
  );
});

export default StorefrontSeo;
